import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PanelStateContext } from './context';
import {
  IEntitySelectionPanelProps,
  ILoadableNode,
  IPanelStateContext,
  ISearchTagsState,
} from './types';
import { PanelRow, PanelWrapper } from './EntitySelectionPanelUI';
import PanelStack from './PanelStack';
import { useEntityLoader } from './useEntityLoader';
import {
  canSelectNestedField,
  createSearchTagsState,
  toNestedFieldNode,
} from './utils';
import { isNil } from 'lodash';
import { assertCondition } from '../../../utils/types';
import { useNestedFieldState } from './useNestedFieldState';
import useInlineBranchState from './useInlineBranchState';
import { useDebounce } from '../../../utils/hooks';
import { useQuery } from '@apollo/client';
import {
  ISearchTagsQuery,
  ISearchTagsQueryVariables,
} from '../../../utils/apollo/graphqlSchema';
import { SEARCH_TAGS } from '../../../utils/apollo/entitySelection/query';
import Loading from './panel/Loading';
import { useUserSettingsSelector } from '../../../redux/selectorHooks';
import { DefaultView } from '../../../utils/userSettings';

const EntitySelectionPanel = ({
  onClose,
  onCreateEntity,
  onSelectEntity,
  schemaId,
  searchTerm,
  selectedEntities,
  tags,
  setSearchTerm,
  disabledEntities,
  onCreateCustom,
  setLoading,
}: IEntitySelectionPanelProps) => {
  const defaultView = useUserSettingsSelector('defaultView');
  const [selectingEntityInProgress, setSelectingEntityInProgress] =
    useState(false);

  const { getNestedFieldState, setNestedFieldState, getValidatedNestedField } =
    useNestedFieldState(selectedEntities);

  const { toggleInlineBranch, getIsExpandedInlineBranch } =
    useInlineBranchState();

  const {
    getEntityFromCacheOrFetch,
    getEntityFromCache,
    loading: entityLoading,
  } = useEntityLoader(schemaId);
  const selectNodeHandler = useCallback(
    async (node: ILoadableNode) => {
      setSelectingEntityInProgress(true);
      try {
        const currentEntity = await getEntityFromCacheOrFetch(node);

        if (currentEntity.__typename === '__CreatableEntity') {
          onCreateEntity?.(searchTerm);
        } else if (currentEntity.__typename === '__NestedFieldEntity') {
          setNestedFieldState(currentEntity, (draft) => {
            draft.error = null;
          });
          const validatedNestedField = getValidatedNestedField(currentEntity);
          if (isNil(validatedNestedField)) {
            return;
          }
          const nestedFieldNode = toNestedFieldNode(validatedNestedField);
          setSelectingEntityInProgress(true);
          const loadedEntity = await getEntityFromCacheOrFetch(nestedFieldNode);

          assertCondition(loadedEntity?.__typename === 'Field');
          if (
            !canSelectNestedField(
              selectedEntities,
              disabledEntities,
              loadedEntity,
            )
          ) {
            setNestedFieldState(currentEntity, (draft) => {
              draft.error =
                "You can't use this configuration of key and type. Please select different key or type.";
            });
            return;
          }
          onSelectEntity?.(loadedEntity);
        } else {
          onSelectEntity?.(currentEntity);
        }
        setSearchTerm('');
        onClose();
      } finally {
        setSelectingEntityInProgress(false);
      }
    },
    [
      disabledEntities,
      getValidatedNestedField,
      getEntityFromCacheOrFetch,
      onClose,
      onCreateEntity,
      onSelectEntity,
      searchTerm,
      selectedEntities,
      setNestedFieldState,
      setSearchTerm,
    ],
  );

  const validSearchTerm = searchTerm.trim() !== '';
  const isCreatable = Boolean(
    onCreateEntity && onSelectEntity && validSearchTerm,
  );

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const [searchTagsState, setSearchTagsState] =
    useState<ISearchTagsState | null>(null);
  const { loading: searchLoading, refetch: refreshSearch } = useQuery<
    ISearchTagsQuery,
    ISearchTagsQueryVariables
  >(SEARCH_TAGS, {
    variables: {
      schemaId,
      searchTerm: debouncedSearchTerm,
      tags,
      managedOnly: defaultView === DefaultView.ManagedView,
    },
    onCompleted: (data) => {
      setSearchTagsState(createSearchTagsState(data.searchTags));
    },
    // when onChange is executed, the tags may change (see: KBT-475) due to
    // onChange callback may influence `tags`. We don't need this query to be
    // executed at all so we skip in the final moment of this components
    // lifecycle (because `onClose` usually unmounts it)
    skip: selectingEntityInProgress,
  });

  // show indication in the entity selection (React Select) input box
  // that search is running
  useEffect(() => {
    setLoading(searchLoading);

    // if component unmounts -> clear the dropdown loading
    return () => setLoading(false);
  }, [searchLoading, setLoading]);

  const panelStateContext = useMemo(
    (): IPanelStateContext => ({
      schemaId,
      searchTerm,
      selectedEntities,
      onClose,
      tags,
      isCreatable,
      selectNodeHandler,
      getEntityFromCache,
      getEntityFromCacheOrFetch,
      entityLoading,
      getNestedFieldState,
      setNestedFieldState,
      getValidatedNestedField,
      disabledEntities,
      onCreateCustom,
      toggleInlineBranch,
      getIsExpandedInlineBranch,
      refreshSearch,
    }),
    [
      schemaId,
      searchTerm,
      selectedEntities,
      onClose,
      tags,
      isCreatable,
      selectNodeHandler,
      getEntityFromCache,
      getEntityFromCacheOrFetch,
      entityLoading,
      getNestedFieldState,
      setNestedFieldState,
      getValidatedNestedField,
      disabledEntities,
      onCreateCustom,
      toggleInlineBranch,
      getIsExpandedInlineBranch,
      refreshSearch,
    ],
  );

  return (
    <PanelStateContext.Provider value={panelStateContext}>
      <PanelWrapper>
        <PanelRow $disabled={selectingEntityInProgress}>
          {!isNil(searchTagsState) ? (
            <PanelStack searchTagsState={searchTagsState} />
          ) : (
            searchLoading && <Loading />
          )}
        </PanelRow>
      </PanelWrapper>
    </PanelStateContext.Provider>
  );
};
export default EntitySelectionPanel;
